import { useState, useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ScanLine, Loader2, ChevronDown, X } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Card,
  Badge,
  EmptyState,
  Skeleton,
  Input,
  Select,
  Pagination,
} from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { LprVerificationDrawer } from '@/components/lpr/LprVerificationDrawer'
import { SettleParkingSessionModal } from '@/components/lpr/SettleParkingSessionModal'
import { useLprEvents, useLprAttributeFacets } from '@/hooks/useLprEvents'
import { useProperty } from '@/hooks/useProperties'
import { useDateTimeFormatter, useTimeFormatter } from '@/hooks/useDateFormatter'
import { useAuth } from '@/hooks/useAuth'
import type { LprEvent, LprVerificationStatus } from '@/types/parking'

const apiBase = import.meta.env.VITE_API_URL || ''

function thumbUrl(key: string): string {
  return key.startsWith('/api/images/') ? `${apiBase}${key}` : `${apiBase}/api/images/${key}`
}

function todayString(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

type VerificationFilter = LprVerificationStatus | 'all'

function VerificationBadge({ status }: { status: LprVerificationStatus }) {
  const { t } = useTranslation('parking')
  switch (status) {
    case 'verified_success':
      return <Badge variant="success">{t('lpr.verification_statuses.verified_success')}</Badge>
    case 'verified_failed':
      return <Badge variant="error">{t('lpr.verification_statuses.verified_failed')}</Badge>
    case 'pending':
    default:
      return <Badge variant="warning">{t('lpr.verification_statuses.pending')}</Badge>
  }
}

// T148-007 (EP-148 Part B, spec §4.4/§5): the "unpaid candidate" / "resolved,
// paid" signal for a row. Renders nothing when the event was never linked to
// a parking session. Never both badges at once — resolved-paid supersedes
// the candidate badge outright, per the AC.
function LprCandidateBadge({ event }: { event: LprEvent }) {
  const { t } = useTranslation('parking')
  if (event.parking_session_id == null) return null

  if (event.unpaid_candidate_paid) {
    return <Badge variant="success">{t('lpr.candidate.resolved_paid')}</Badge>
  }

  const uc = event.unpaid_candidate
  const title = uc
    ? t('lpr.candidate.tooltip', {
        distance: uc.distance,
        confidence: Math.round(uc.confidence * 100),
      })
    : undefined

  return (
    <div className="flex flex-col items-start gap-0.5" data-testid="lpr-candidate-badge">
      <Badge variant="warning" title={title}>
        {t('lpr.candidate.badge')}
      </Badge>
      <span className="text-xs text-base-content/60">
        {t('lpr.candidate.session_label', { id: event.parking_session_id })}
      </span>
    </div>
  )
}

function ThumbCell({ event }: { event: LprEvent }) {
  const { t } = useTranslation('parking')
  if (!event.front_thumb_key || event.thumb_fetch_status === 'failed') {
    return (
      <div
        className="w-12 h-12 bg-base-200 rounded flex items-center justify-center"
        aria-label={t('lpr.thumbnail_unavailable')}
        data-testid="thumb-placeholder"
      >
        <ScanLine className="w-5 h-5 text-base-content/30" />
      </div>
    )
  }
  return (
    <img
      src={thumbUrl(event.front_thumb_key)}
      alt={t('lpr.thumbnail_alt')}
      className="w-12 h-12 object-cover rounded"
    />
  )
}

// T148A-007: an unresolved candidate row — the settle action only ever
// appears here, never once the candidate is already resolved-paid, and never
// for a row that was never linked to a parking session.
function isUnresolvedCandidate(event: LprEvent): boolean {
  return event.parking_session_id != null && !event.unpaid_candidate_paid
}

function EventRow({
  event,
  selected,
  onSelect,
  formatDateTime,
  canSettle,
  onSettle,
}: {
  event: LprEvent
  selected: boolean
  onSelect: (id: number) => void
  formatDateTime: (d: string | null | undefined) => string
  canSettle: boolean
  onSettle: (event: LprEvent) => void
}) {
  const { t } = useTranslation('parking')
  const topId = event.ml_candidate_vehicle_ids?.[0]
  const score = event.ml_re_id_score

  return (
    <tr
      className={`cursor-pointer transition-colors ${
        selected ? 'bg-base-200 border-l-4 border-primary' : 'hover'
      }`}
      onClick={() => onSelect(event.id)}
      aria-selected={selected}
      data-testid={`lpr-row-${event.id}`}
    >
      <td>
        <ThumbCell event={event} />
      </td>
      <td>
        <span className="text-sm">{formatDateTime(event.captured_at)}</span>
      </td>
      <td>
        <span className="font-medium font-mono">{event.plate_text ?? '—'}</span>
      </td>
      <td>
        <span className="text-sm">{event.camera_id ?? '—'}</span>
      </td>
      <td>
        {topId ? (
          <span className="text-sm">
            #{topId}
            {score != null && (
              <span className="ml-1 text-base-content/50">({(score * 100).toFixed(0)}%)</span>
            )}
          </span>
        ) : (
          <span className="text-base-content/40">—</span>
        )}
      </td>
      <td>
        <div className="flex flex-col items-start gap-1">
          <VerificationBadge status={event.verification_status} />
          <LprCandidateBadge event={event} />
          {canSettle && isUnresolvedCandidate(event) && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                onSettle(event)
              }}
              data-testid={`btn-settle-${event.id}`}
            >
              {t('lpr.settle.action')}
            </Button>
          )}
        </div>
      </td>
    </tr>
  )
}

// Mobile card for a single LPR event — text-only, no thumbnail
function EventCard({
  event,
  selected,
  onSelect,
  formatTime,
  canSettle,
  onSettle,
}: {
  event: LprEvent
  selected: boolean
  onSelect: (id: number) => void
  formatTime: (d: string | null | undefined) => string
  canSettle: boolean
  onSettle: (event: LprEvent) => void
}) {
  const { t, i18n } = useTranslation('parking')
  const topId = event.ml_candidate_vehicle_ids?.[0]
  const score = event.ml_re_id_score

  // Render direction via exists()-guard so raw ML values (in/out/parked/unclear)
  // are covered by the new keys while legacy (entry/exit) stay intact.
  const directionLabel = event.direction
    ? i18n.exists(`lpr.directions.${event.direction}`, { ns: 'parking' })
      ? t(`lpr.directions.${event.direction}`)
      : event.direction
    : '—'

  return (
    <div
      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
        selected
          ? 'bg-base-200 border-primary border-l-4'
          : 'bg-base-100 border-base-300 hover:bg-base-200'
      }`}
      onClick={() => onSelect(event.id)}
      aria-selected={selected}
      data-testid={`lpr-card-${event.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium font-mono text-sm">
              {event.plate_text ?? '—'}
            </span>
            <span className="text-xs text-base-content/60">
              {directionLabel}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-base-content/60 flex-wrap">
            <span>{formatTime(event.captured_at)}</span>
            {topId != null && (
              <span>
                #{topId}
                {score != null && (
                  <span className="ml-0.5">({(score * 100).toFixed(0)}%)</span>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <VerificationBadge status={event.verification_status} />
          <LprCandidateBadge event={event} />
          {canSettle && isUnresolvedCandidate(event) && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                onSettle(event)
              }}
              data-testid={`btn-settle-${event.id}`}
            >
              {t('lpr.settle.action')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export function LprEventLogPage() {
  const { t, i18n } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const formatDateTime = useDateTimeFormatter()
  const formatTime = useTimeFormatter()
  const { propertyId: propertyIdParam } = useParams<{ propertyId: string }>()
  const propertyId = Number(propertyIdParam)

  const [dateFrom, setDateFrom] = useState(todayString)
  const [dateTo, setDateTo] = useState(todayString)
  // Default to 'pending' so admins see the work queue first
  const [verificationFilter, setVerificationFilter] = useState<VerificationFilter>('pending')
  const [page, setPage] = useState(1)
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  // Mobile: whether the filter block is expanded
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // T148A-007: which candidate row is being settled — drives the
  // SettleParkingSessionModal. Client-side defense-in-depth gate; the real
  // gate is ParkingSessionSettlementPolicy#create? on the server (T148A-006).
  const { user } = useAuth()
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const [settlingEvent, setSettlingEvent] = useState<LprEvent | null>(null)

  // EP-106: attribute filters
  const [plateInput, setPlateInput] = useState('')
  const [plateText, setPlateText] = useState('')
  const [vehicleType, setVehicleType] = useState('')
  const [color, setColor] = useState('')
  const [direction, setDirection] = useState('')

  // T148-007 (EP-148 Part B): standalone client-side filter, orthogonal to
  // verificationFilter — candidate-ness is not a verification-status value,
  // so it never joins the exclusive filterChips array.
  const [candidatesOnly, setCandidatesOnly] = useState(false)

  // EP-106: Q1 — one-shot status flip when an attribute filter is first set
  // while verificationFilter is still 'pending'. Ref is set once the user
  // makes an explicit status choice so we never override it.
  const userSetStatusRef = useRef(false)

  // EP-106: debounce plate input ~300ms before sending to query
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPlateText(plateInput)
      setPage(1)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [plateInput])

  // EP-106: Q1 — one-shot status flip: when any attribute filter goes from
  // empty → set AND verification is still initial 'pending' AND user hasn't
  // explicitly changed status, flip once to 'all'.
  useEffect(() => {
    if (userSetStatusRef.current) return
    const anyActive = !!(plateText || vehicleType || color || direction)
    if (anyActive && verificationFilter === 'pending') {
      setVerificationFilter('all')
    }
    // NOTE: intentionally not watching verificationFilter in deps — we only
    // want to fire when the attribute states change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plateText, vehicleType, color, direction])

  const { data: property } = useProperty(propertyId)

  // EP-106: fetch attribute facets (property-scoped when propertyId is set)
  const { data: facets } = useLprAttributeFacets(propertyId || undefined)

  const { data, isLoading, isFetching, error } = useLprEvents({
    property_id: propertyId || undefined,
    verification_status: verificationFilter === 'all' ? '' : verificationFilter,
    captured_at_from: dateFrom,
    captured_at_to: dateTo,
    page,
    // EP-106: attribute filters
    plate_text: plateText || undefined,
    vehicle_type: vehicleType || undefined,
    color: color || undefined,
    direction: direction || undefined,
  })

  const events = data?.data ?? []

  // T148-007: candidate-only narrowing is purely client-side on the already
  // fetched page — it does not touch the useLprEvents query params or
  // verificationFilter state.
  const filteredEvents = candidatesOnly
    ? events.filter((e) => e.parking_session_id != null)
    : events

  // Ordered list of pending event ids (for drawer ↑/↓ nav)
  const pendingEventIds = events
    .filter((e) => e.verification_status === 'pending')
    .map((e) => e.id)

  const selectedEvent = events.find((e) => e.id === selectedEventId) ?? null

  // Navigate to prev/next pending row from within the drawer
  const handleDrawerNavigate = useCallback(
    (direction: 'up' | 'down') => {
      if (!selectedEventId) return
      const idx = pendingEventIds.indexOf(selectedEventId)
      if (direction === 'down') {
        const nextId = pendingEventIds[idx + 1] ?? pendingEventIds[0] ?? null
        setSelectedEventId(nextId)
      } else {
        const prevId =
          pendingEventIds[idx - 1] ?? pendingEventIds[pendingEventIds.length - 1] ?? null
        setSelectedEventId(prevId)
      }
    },
    [selectedEventId, pendingEventIds],
  )

  // D9 (EP-147): the lpr_enabled module gate used to be checked here
  // (single-gate, no Unleash flag). It moved to the route level, into the lpr
  // ModuleRoute wrapping this page in App.tsx, so the sidebar and the guard
  // share one predicate (useModuleEnabled) instead of this page re-deriving
  // its own copy.

  const filterChips: { value: VerificationFilter; label: string }[] = [
    { value: 'all', label: t('lpr.filters.all') },
    { value: 'pending', label: t('lpr.filters.pending') },
    { value: 'verified_success', label: t('lpr.filters.verified') },
    { value: 'verified_failed', label: t('lpr.filters.failed') },
  ]

  function handleSelectEvent(id: number) {
    setSelectedEventId((prev) => (prev === id ? null : id))
  }

  // EP-106: helper — render a facet value via i18n.exists() guard so unmapped
  // values fall back to the raw string (established feeTable pattern).
  function facetLabel(ns: 'vehicle_types' | 'colors' | 'directions', v: string): string {
    const key = `lpr.${ns}.${v}`
    return i18n.exists(key, { ns: 'parking' }) ? t(`lpr.${ns}.${v}` as Parameters<typeof t>[0]) : v
  }

  // EP-106: build Select options from facets with 'All' as first option
  const typeOptions = [
    { value: '', label: t('lpr.filters.all') },
    ...(facets?.vehicle_types ?? []).map((v) => ({ value: v, label: facetLabel('vehicle_types', v) })),
  ]
  const colorOptions = [
    { value: '', label: t('lpr.filters.all') },
    ...(facets?.colors ?? []).map((v) => ({ value: v, label: facetLabel('colors', v) })),
  ]
  const directionOptions = [
    { value: '', label: t('lpr.filters.all') },
    ...(facets?.directions ?? []).map((v) => ({ value: v, label: facetLabel('directions', v) })),
  ]

  // EP-106: attribute filter clear (resets only the 4 attribute fields, not date/status)
  function clearAttributeFilters() {
    setPlateInput('')
    setPlateText('')
    setVehicleType('')
    setColor('')
    setDirection('')
    setPage(1)
  }

  const anyAttributeActive = !!(plateText || vehicleType || color || direction)

  // EP-106: active filter badge count — verification (when not 'all') + 4 attribute fields
  const activeFilterCount =
    (verificationFilter !== 'all' ? 1 : 0) +
    (plateText ? 1 : 0) +
    (vehicleType ? 1 : 0) +
    (color ? 1 : 0) +
    (direction ? 1 : 0)

  // EP-106: Q2 — show widen-date hint when filtered result is empty
  const showWidenDateHint = !isLoading && !error && events.length === 0 && anyAttributeActive

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: tc('nav.properties'), href: '/properties' },
          { label: property?.name ?? '...', href: `/properties/${propertyId}` },
          { label: t('lpr.title') },
        ]}
      />

      <PageHeader
        title={t('lpr.title')}
        description={t('lpr.description')}
      />

      {/* Filters */}
      <Card>
        {/* Mobile: collapsible filter toggle */}
        <div className="lg:hidden">
          <button
            type="button"
            className="flex w-full items-center justify-between text-sm font-medium"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            aria-expanded={mobileFiltersOpen}
            data-testid="mobile-filter-toggle"
          >
            <span>
              {t('lpr.filters.filter_label')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2" data-testid="mobile-filter-badge">
                  {t('lpr.filters.active_count', { count: activeFilterCount })}
                </Badge>
              )}
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${mobileFiltersOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {/* Filter body: hidden on mobile when collapsed, always visible on lg+ */}
        <div className={`${mobileFiltersOpen ? '' : 'hidden'} lg:block`} data-testid="filter-body">
          <div className="flex flex-wrap items-end gap-4 pt-3 lg:pt-0">
            {/* Verification status chips */}
            <div className="flex flex-wrap gap-2" role="group" aria-label={t('lpr.verification_status')}>
              {filterChips.map((chip) => (
                <Button
                  key={chip.value}
                  type="button"
                  size="sm"
                  variant={verificationFilter === chip.value ? 'primary' : 'ghost'}
                  onClick={() => {
                    // Mark that the user explicitly chose a status — never override again
                    userSetStatusRef.current = true
                    setVerificationFilter(chip.value)
                    setPage(1)
                  }}
                  aria-pressed={verificationFilter === chip.value}
                  data-testid={`filter-chip-${chip.value}`}
                >
                  {chip.label}
                </Button>
              ))}
            </div>
            <div className="w-40">
              <Input
                label={t('lpr.filters.date_from')}
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(1)
                }}
              />
            </div>
            <div className="w-40">
              <Input
                label={t('lpr.filters.date_to')}
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(1)
                }}
              />
            </div>

            {/* EP-106: Plate search input with clear button */}
            <div className="min-w-0 w-40">
              <div className="relative">
                <Input
                  label={t('lpr.plate_text')}
                  inputClassName="font-mono pr-8"
                  placeholder={t('lpr.filters.plate_placeholder')}
                  value={plateInput}
                  onChange={(e) => {
                    setPlateInput(e.target.value)
                  }}
                  data-testid="plate-filter-input"
                />
                {plateInput && (
                  <button
                    type="button"
                    aria-label={t('lpr.filters.clear_plate')}
                    className="absolute right-2 top-9 p-0.5 text-base-content/60 hover:text-base-content"
                    onClick={() => {
                      setPlateInput('')
                      setPlateText('')
                      setPage(1)
                    }}
                    data-testid="plate-filter-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* EP-106: Vehicle Type Select */}
            <div className="min-w-0 w-40">
              <Select
                label={t('lpr.filters.vehicle_type')}
                options={typeOptions}
                value={vehicleType}
                onChange={(e) => {
                  setVehicleType(e.target.value)
                  setPage(1)
                }}
                data-testid="vehicle-type-filter"
              />
            </div>

            {/* EP-106: Color Select */}
            <div className="min-w-0 w-40">
              <Select
                label={t('lpr.filters.color')}
                options={colorOptions}
                value={color}
                onChange={(e) => {
                  setColor(e.target.value)
                  setPage(1)
                }}
                data-testid="color-filter"
              />
            </div>

            {/* EP-106: Direction Select — data-driven, not hardcoded */}
            <div className="min-w-0 w-40">
              <Select
                label={t('lpr.filters.direction')}
                options={directionOptions}
                value={direction}
                onChange={(e) => {
                  setDirection(e.target.value)
                  setPage(1)
                }}
                data-testid="direction-filter"
              />
            </div>

            {/* EP-106: Clear attribute filters button (ghost, shown when any active) */}
            {anyAttributeActive && (
              <div className="flex items-end">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={clearAttributeFilters}
                  data-testid="clear-attribute-filters"
                >
                  {t('lpr.filters.clear')}
                </button>
              </div>
            )}

            {/* T148-007: standalone toggle, deliberately separate from the
                exclusive verification-status filterChips array above — this
                narrows to candidate rows without touching verificationFilter. */}
            <div className="flex items-end">
              <Button
                type="button"
                size="sm"
                variant={candidatesOnly ? 'primary' : 'ghost'}
                onClick={() => {
                  setCandidatesOnly((prev) => !prev)
                  setPage(1)
                }}
                aria-pressed={candidatesOnly}
                data-testid="candidates-only-toggle"
              >
                {t('lpr.filters.candidates_only')}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={tc('errors.loading_failed')}
            description={tc('errors.try_again')}
          />
        </Card>
      ) : !filteredEvents.length ? (
        <Card>
          <EmptyState
            icon={<ScanLine className="w-12 h-12" />}
            title={t('lpr.empty.title')}
            description={
              showWidenDateHint
                ? t('lpr.filters.widen_date_hint')
                : t('lpr.empty.description')
            }
          />
        </Card>
      ) : (
        <Card>
          {isFetching && !isLoading && (
            <div className="flex justify-end mb-2">
              <Loader2
                className="w-4 h-4 animate-spin text-base-content/40"
                aria-label={tc('labels.refreshing')}
              />
            </div>
          )}

          {/* Desktop table — hidden on mobile */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="table lg:min-w-max">
              <thead>
                <tr>
                  <th>{t('lpr.thumbnail')}</th>
                  <th>{t('lpr.captured_at')}</th>
                  <th>{t('lpr.plate_text')}</th>
                  <th>{t('lpr.camera')}</th>
                  <th>{t('lpr.best_candidate')}</th>
                  <th>{t('lpr.verification_status')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    selected={event.id === selectedEventId}
                    onSelect={handleSelectEvent}
                    formatDateTime={formatDateTime}
                    canSettle={isAdminOrOwner}
                    onSettle={setSettlingEvent}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list — hidden on lg+ */}
          <div className="lg:hidden space-y-2" data-testid="mobile-card-list">
            {filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                selected={event.id === selectedEventId}
                onSelect={handleSelectEvent}
                formatTime={formatTime}
                canSettle={isAdminOrOwner}
                onSettle={setSettlingEvent}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Verification drawer — renders when a row is selected */}
      {selectedEvent && (
        <LprVerificationDrawer
          event={selectedEvent}
          pendingEventIds={pendingEventIds}
          onClose={() => setSelectedEventId(null)}
          onNavigate={handleDrawerNavigate}
        />
      )}

      {/* T148A-007: Settle & check out modal — renders when a candidate row's
          settle action is clicked. parking_session_id is guaranteed non-null
          here (the button only renders on an unresolved-candidate row). */}
      {settlingEvent && settlingEvent.parking_session_id != null && (
        <SettleParkingSessionModal
          parkingSessionId={settlingEvent.parking_session_id}
          onClose={() => setSettlingEvent(null)}
        />
      )}
    </div>
  )
}
